import React, { useState, useEffect, useCallback } from 'react';
import { base44 } from '@/api/base44Client';
import { useAuth } from '@/lib/AuthContext';
import { Link } from 'react-router-dom';
import {
  Loader2, ShieldOff, Trophy, RefreshCw, Users, Mail, Phone, MapPin, Search,
  Crown, CheckCircle2, ArrowLeft, Megaphone, Trash2, Cpu,
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { fmtDate } from '@/lib/utils';
import ConfirmDialog from '@/components/admin/ConfirmDialog';
import CreateCompetitionPostModal from '@/components/admin/CreateCompetitionPostModal';
import AddPostalEntryModal from '@/components/admin/AddPostalEntryModal';
import CompetitionEntryDetailModal from '@/components/admin/CompetitionEntryDetailModal';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function AdminCompetition() {
  const { user: currentUser, isLoadingAuth: authLoading } = useAuth();
  const { toast } = useToast();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningMonthly, setRunningMonthly] = useState(false);
  const [runningRig, setRunningRig] = useState(false);
  const [result, setResult] = useState(null);
  const [rigResult, setRigResult] = useState(null);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState('all'); // all | monthly_free | sim_rig_paid
  const [statusFilter, setStatusFilter] = useState('all'); // all | winners | month
  const [search, setSearch] = useState('');
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [showPostal, setShowPostal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [userEmails, setUserEmails] = useState(new Set());

  const now = new Date();
  const prevMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  prevMonth.setMonth(prevMonth.getMonth() - 1);
  const [month, setMonth] = useState(prevMonth.getMonth() + 1);
  const [year, setYear] = useState(prevMonth.getFullYear());

  // Default rig draw period = most recently completed half-year.
  const defaultRigHalf = now.getMonth() < 6 ? 2 : 1;
  const defaultRigYear = now.getMonth() < 6 ? now.getFullYear() - 1 : now.getFullYear();
  const [rigYear, setRigYear] = useState(defaultRigYear);
  const [rigHalf, setRigHalf] = useState(defaultRigHalf);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rows, users] = await Promise.all([
        base44.entities.CompetitionEntry.list('-created_date', 1000),
        base44.entities.User.list('-created_date', 500).catch(() => []),
      ]);
      setEntries(rows || []);
      setUserEmails(new Set((users || []).map((u) => (u.email || '').toLowerCase())));
    } catch (e) {
      setError(e?.message || 'Failed to load entries');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runMonthlyDraw = async () => {
    setRunningMonthly(true);
    setError('');
    setResult(null);
    try {
      const res = await base44.functions.invoke('drawCompetitionWinner', { month, year, force: true });
      if (res?.error) throw new Error(res.error);
      setResult(res);
      toast({
        title: res.already_drawn ? 'Already drawn' : res.winner ? 'Winner drawn!' : 'No entrants',
        description: res.already_drawn
          ? `${res.month} already has a winner: ${res.winner.first_name} ${res.winner.last_name}. Re-run with a new month to override.`
          : res.winner
            ? `${res.winner.first_name} ${res.winner.last_name} (${res.winner.email}) — email sent + social draft created.`
            : `No entries for ${MONTH_NAMES[month - 1]} ${year}.`,
      });
      await load();
    } catch (err) {
      setError(err.message || 'Draw failed.');
    } finally {
      setRunningMonthly(false);
    }
  };

  const runRigDraw = async () => {
    setRunningRig(true);
    setError('');
    setRigResult(null);
    try {
      const res = await base44.functions.invoke('drawSimRigWinner', { year: rigYear, half: rigHalf });
      if (res?.error) throw new Error(res.error);
      setRigResult(res);
      toast({
        title: res.winner ? 'Sim rig winner drawn!' : 'No entrants',
        description: res.winner
          ? `${res.winner.first_name} ${res.winner.last_name} (${res.winner.email}) — email sent + social draft created.`
          : `No paid entries for ${rigYear}-H${rigHalf}.`,
      });
      await load();
    } catch (err) {
      setError(err.message || 'Sim rig draw failed.');
    } finally {
      setRunningRig(false);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  const deleteEntry = async () => {
    if (!deleteTarget) return;
    try {
      await base44.entities.CompetitionEntry.delete(deleteTarget.id);
      setEntries((prev) => prev.filter((e) => e.id !== deleteTarget.id));
      toast({ title: 'Entry deleted' });
      setDeleteTarget(null);
    } catch (e) {
      toast({ title: 'Delete failed', description: e.message, variant: 'destructive' });
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }
  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
        </div>
      </div>
    );
  }

  const monthKey = `${year}-${String(month).padStart(2, '0')}`;
  const rigPeriod = `${rigYear}-H${rigHalf}`;

  const totalEntries = entries.length;
  const totalWinners = entries.filter((e) => e.is_winner).length;
  const marketingOptIns = entries.filter((e) => e.marketing_opt_in).length;
  const paidEntries = entries.filter((e) => e.competition_type === 'sim_rig_paid' || e.is_paid).length;
  const paidRevenue = entries.filter((e) => e.is_paid).reduce((s, e) => s + (e.paid_amount || 0), 0);

  const filtered = entries.filter((e) => {
    if (typeFilter === 'monthly_free' && e.competition_type === 'sim_rig_paid') return false;
    if (typeFilter === 'sim_rig_paid' && e.competition_type !== 'sim_rig_paid') return false;
    if (statusFilter === 'winners' && !e.is_winner) return false;
    if (statusFilter === 'month' && e.draw_month !== monthKey && e.winner_month !== monthKey) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = `${e.first_name} ${e.last_name} ${e.email} ${e.phone || ''} ${e.postcode || ''} ${e.instagram_handle || ''} ${e.tiktok_handle || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      <section className="py-12 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <Trophy className="w-6 h-6 text-primary" />
            <p className="text-primary text-xs font-bold tracking-widest">MARKETING &amp; COMMS</p>
          </div>
          <h1 className="text-white font-black text-2xl sm:text-3xl md:text-4xl uppercase">Sim Experience & Sim Rig Draws</h1>
          <p className="text-white/30 text-sm mt-1 max-w-xl">
            Customers enter at <Link to="/competition" className="text-primary/70 hover:text-primary">/competition</Link>. Run the free monthly experience draw and the six-monthly paid sim rig draw below — winners are emailed automatically and social post drafts are created.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              onClick={() => setShowCreatePost(true)}
              className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-5 py-3 transition-colors"
            >
              <Megaphone className="w-4 h-4" /> CREATE MEGA COMPETITION POST
            </button>
            <button
              onClick={() => setShowPostal(true)}
              className="inline-flex items-center gap-2 border border-green-500/40 bg-green-500/10 hover:bg-green-500/20 text-green-400 font-bold text-xs tracking-widest px-5 py-3 transition-colors"
            >
              <Mail className="w-4 h-4" /> ADD FREE POSTAL ENTRY
            </button>
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-10 pt-8 pb-12 max-w-6xl mx-auto space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat icon={Users} label="TOTAL ENTRIES" value={totalEntries} />
          <Stat icon={Crown} label="WINNERS" value={totalWinners} />
          <Stat icon={Megaphone} label="OPT-INS" value={marketingOptIns} />
          <Stat icon={Cpu} label="PAID ENTRIES · £" value={paidRevenue} />
        </div>

        {/* Monthly draw control */}
        <div className="border border-white/10 bg-[#111115] p-6">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-4 h-4 text-primary" />
            <p className="text-white/50 text-[10px] font-bold tracking-widest">RUN FREE MONTHLY DRAW</p>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-end gap-4">
            <div>
              <label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">MONTH</label>
              <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="bg-[#0d0d0f] border border-white/10 text-white text-sm font-bold h-10 px-3 focus:outline-none focus:border-primary/60">
                {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1} className="bg-[#0d0d0f]">{m}</option>)}
              </select>
            </div>
            <div>
              <label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">YEAR</label>
              <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="bg-[#0d0d0f] border border-white/10 text-white text-sm font-bold h-10 px-3 focus:outline-none focus:border-primary/60">
                {[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() + 1].map((y) => <option key={y} value={y} className="bg-[#0d0d0f]">{y}</option>)}
              </select>
            </div>
            <button onClick={runMonthlyDraw} disabled={runningMonthly} className="bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-6 py-2.5 h-10 transition-colors disabled:opacity-50 flex items-center gap-2">
              {runningMonthly ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} RUN DRAW
            </button>
          </div>
          {result && (
            <div className="mt-4 pt-4 border-t border-white/5">
              <p className="text-white/40 text-[10px] font-bold tracking-widest mb-1">RESULT — {result.month}</p>
              {result.winner ? (
                <div className="flex items-center gap-3">
                  <Crown className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-white font-bold text-sm">{result.winner.first_name} {result.winner.last_name}</p>
                    <p className="text-white/40 text-xs">{result.winner.email} · {result.entrants} entrants</p>
                  </div>
                </div>
              ) : <p className="text-white/40 text-xs">{result.message || 'No entries.'}</p>}
            </div>
          )}
        </div>

        {/* Sim rig draw control */}
        <div className="border border-primary/20 bg-primary/[0.03] p-6">
          <div className="flex items-center gap-2 mb-4">
            <Cpu className="w-4 h-4 text-primary" />
            <p className="text-primary text-[10px] font-bold tracking-widest">RUN SIX-MONTHLY SIM RIG DRAW (£5 PAID ENTRIES)</p>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-end gap-4">
            <div>
              <label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">YEAR</label>
              <select value={rigYear} onChange={(e) => setRigYear(Number(e.target.value))} className="bg-[#0d0d0f] border border-white/10 text-white text-sm font-bold h-10 px-3 focus:outline-none focus:border-primary/60">
                {[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map((y) => <option key={y} value={y} className="bg-[#0d0d0f]">{y}</option>)}
              </select>
            </div>
            <div>
              <label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">HALF</label>
              <select value={rigHalf} onChange={(e) => setRigHalf(Number(e.target.value))} className="bg-[#0d0d0f] border border-white/10 text-white text-sm font-bold h-10 px-3 focus:outline-none focus:border-primary/60">
                <option value={1} className="bg-[#0d0d0f]">H1 (Jan–Jun)</option>
                <option value={2} className="bg-[#0d0d0f]">H2 (Jul–Dec)</option>
              </select>
            </div>
            <button onClick={runRigDraw} disabled={runningRig} className="bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-6 py-2.5 h-10 transition-colors disabled:opacity-50 flex items-center gap-2">
              {runningRig ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} RUN SIM RIG DRAW
            </button>
          </div>
          <p className="text-white/30 text-[11px] mt-3 leading-relaxed">
            Drawing for {rigPeriod} picks from all paid (£5) entries in that half-year. Winner receives an email and a draft social post is created. Re-running replaces the winner.
          </p>
          {rigResult && (
            <div className="mt-4 pt-4 border-t border-white/5">
              <p className="text-white/40 text-[10px] font-bold tracking-widest mb-1">RESULT — {rigResult.period}</p>
              {rigResult.winner ? (
                <div className="flex items-center gap-3">
                  <Crown className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-white font-bold text-sm">{rigResult.winner.first_name} {rigResult.winner.last_name}</p>
                    <p className="text-white/40 text-xs">{rigResult.winner.email} · {rigResult.entrants} entrants</p>
                  </div>
                </div>
              ) : <p className="text-white/40 text-xs">{rigResult.message || 'No paid entries.'}</p>}
            </div>
          )}
        </div>

        {error && <div className="border border-red-500/20 bg-red-500/5 text-red-400 text-xs px-4 py-3">{error}</div>}

        {/* Filters + search */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap">
          <div className="flex gap-1">
            {[['all', 'ALL TYPES'], ['monthly_free', 'FREE'], ['sim_rig_paid', 'PAID']].map(([key, label]) => (
              <button key={key} onClick={() => setTypeFilter(key)} className={`px-3 py-2 text-[10px] font-bold tracking-widest border transition-colors ${typeFilter === key ? 'border-primary text-white bg-primary/10' : 'border-white/10 text-white/40 hover:text-white/70'}`}>{label}</button>
            ))}
          </div>
          <div className="flex gap-1">
            {[['all', 'ALL'], ['winners', 'WINNERS'], ['month', monthKey]].map(([key, label]) => (
              <button key={key} onClick={() => setStatusFilter(key)} className={`px-3 py-2 text-[10px] font-bold tracking-widest border transition-colors ${statusFilter === key ? 'border-primary text-white bg-primary/10' : 'border-white/10 text-white/40 hover:text-white/70'}`}>{label}</button>
            ))}
          </div>
          <div className="relative flex-1 max-w-xs">
            <Search className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, handle…" className="w-full bg-[#0d0d0f] border border-white/10 text-white text-sm pl-9 pr-3 h-10 focus:outline-none focus:border-primary/60" />
          </div>
        </div>

        {/* Entries table */}
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : (
          <div className="border border-white/10 bg-[#111115] overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#0d0d0f] border-b border-white/5">
                  <th className="text-left px-4 py-2.5 text-white/30 text-[10px] font-bold tracking-widest">NAME</th>
                  <th className="text-left px-3 py-2.5 text-white/30 text-[10px] font-bold tracking-widest">CONTACT</th>
                  <th className="text-left px-3 py-2.5 text-white/30 text-[10px] font-bold tracking-widest hidden md:table-cell">DOB</th>
                  <th className="text-left px-3 py-2.5 text-white/30 text-[10px] font-bold tracking-widest hidden lg:table-cell">ADDRESS</th>
                  <th className="text-left px-3 py-2.5 text-white/30 text-[10px] font-bold tracking-widest hidden md:table-cell">SOCIAL</th>
                  <th className="text-left px-3 py-2.5 text-white/30 text-[10px] font-bold tracking-widest">TYPE</th>
                  <th className="text-left px-3 py-2.5 text-white/30 text-[10px] font-bold tracking-widest">STATUS</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-12 text-center text-white/30 text-sm">No entries match.</td></tr>
                )}
                {filtered.map((e) => {
                  const isPaid = e.competition_type === 'sim_rig_paid' || e.is_paid;
                  const socialBits = [e.instagram_handle && 'IG', e.tiktok_handle && 'TT', e.facebook_handle && 'FB'].filter(Boolean).join(' · ');
                  return (
                    <tr key={e.id} onClick={() => setSelectedEntry(e)} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors cursor-pointer">
                      <td className="px-4 py-3 text-white/80 text-sm font-medium">
                        {e.first_name} {e.last_name}
                        {e.marketing_opt_in && <span className="ml-2 text-[9px] font-bold tracking-widest text-green-400 border border-green-500/30 px-1.5 py-0.5">OPT-IN</span>}
                        {userEmails.has((e.email || '').toLowerCase()) && <span className="ml-1 text-[9px] font-bold tracking-widest text-primary border border-primary/30 px-1.5 py-0.5">ACCOUNT</span>}
                        {!userEmails.has((e.email || '').toLowerCase()) && e.invited_at && <span className="ml-1 text-[9px] font-bold tracking-widest text-amber-400 border border-amber-500/30 px-1.5 py-0.5">INVITED</span>}
                      </td>
                      <td className="px-3 py-3 text-white/60 text-xs">
                        <div>{e.email}</div>
                        {e.phone && <div className="text-white/30">{e.phone}</div>}
                      </td>
                      <td className="px-3 py-3 text-white/40 text-xs hidden md:table-cell">{fmtDate(e.date_of_birth) || '—'}</td>
                      <td className="px-3 py-3 text-white/40 text-xs hidden lg:table-cell">
                        {e.address_line1 ? <>{e.address_line1}, {e.address_city}, {e.postcode || ''}</> : (e.postcode || '—')}
                      </td>
                      <td className="px-3 py-3 text-white/40 text-xs hidden md:table-cell">{socialBits || '—'}</td>
                      <td className="px-3 py-3">
                        {isPaid
                          ? <span className="inline-flex items-center gap-1 text-primary text-[10px] font-bold tracking-widest border border-primary/30 bg-primary/10 px-2 py-1">SIM RIG £{e.paid_amount || 5} · {e.rig_draw_period}</span>
                          : <span className="inline-flex items-center gap-1 text-white/60 text-[10px] font-bold tracking-widest border border-white/15 bg-white/5 px-2 py-1">MONTHLY FREE</span>}
                      </td>
                      <td className="px-3 py-3">
                        {e.is_winner ? (
                          <span className="inline-flex items-center gap-1 text-green-400 text-[10px] font-bold tracking-widest border border-green-500/30 bg-green-500/5 px-2 py-1">
                            <Crown className="w-3 h-3" /> {e.winner_month}
                          </span>
                        ) : (
                          <span className="text-white/30 text-[10px] tracking-widest">ENTRY</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right" onClick={(ev) => ev.stopPropagation()}>
                        <button onClick={() => setDeleteTarget(e)} className="text-white/20 hover:text-red-400 transition-colors" title="Delete entry">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {showCreatePost && (
        <CreateCompetitionPostModal
          onClose={() => setShowCreatePost(false)}
          onCreated={() => setShowCreatePost(false)}
        />
      )}

      {showPostal && (
        <AddPostalEntryModal
          onClose={() => setShowPostal(false)}
          onCreated={() => { setShowPostal(false); load(); }}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete competition entry"
        description={deleteTarget ? `Delete the entry from ${deleteTarget.first_name} ${deleteTarget.last_name}? This cannot be undone.` : ''}
        confirmLabel="Delete"
        onConfirm={deleteEntry}
        onClose={() => setDeleteTarget(null)}
      />

      {selectedEntry && (
        <CompetitionEntryDetailModal
          entry={selectedEntry}
          hasAccount={userEmails.has((selectedEntry.email || '').toLowerCase())}
          onClose={() => setSelectedEntry(null)}
          onInvited={(id, ts) => setEntries((prev) => prev.map((x) => (x.id === id ? { ...x, invited_at: ts } : x)))}
        />
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="border border-white/10 bg-[#111115] p-4">
      <div className="flex items-center gap-2 text-white/30 mb-2">
        <Icon className="w-4 h-4" />
        <span className="text-[10px] font-bold tracking-widest">{label}</span>
      </div>
      <div className="text-white font-black text-2xl">{value}</div>
    </div>
  );
}